import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { ReceiptText, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/billing";

const BLN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const lbl = (ym) => `${BLN[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;
const compact = (v) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)}M` : v >= 1e6 ? `${(v / 1e6).toFixed(1)}jt` : v >= 1e3 ? `${Math.round(v / 1e3)}rb` : `${v}`);

const Card = ({ label, value, hint, Icon, cls, testid }) => (
  <div className={`rounded-lg border p-3 sm:p-4 ${cls}`} data-testid={testid}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-wide font-semibold opacity-80">{label}</div>
        <div className="mt-1 font-mono font-black text-base sm:text-xl" data-testid={`${testid}-value`}>{value}</div>
        <div className="text-[10px] sm:text-[11px] mt-1 opacity-80" data-testid={`${testid}-hint`}>{hint}</div>
      </div>
      <Icon className="w-5 h-5 sm:w-6 sm:h-6 shrink-0 opacity-80" />
    </div>
  </div>
);

export const NbRekapSection = ({ ym }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let ok = true;
    setErr("");
    api.get("/billing/nb-invoices/rekap", { params: { end_month: ym, months: 12 } })
      .then((r) => ok && setData(r.data)).catch(() => ok && setErr("Gagal memuat rekap Barang/Jasa"));
    return () => { ok = false; };
  }, [ym]);
  const items = data?.items || [];
  const cur = items[items.length - 1] || {};
  const chart = items.map((i) => ({ ...i, label: lbl(i.month) }));
  return (
    <section className="rounded-lg border border-[#BFDBFE] bg-white p-3 sm:p-4 space-y-3" data-testid="nb-rekap-section">
      <div>
        <h2 className="text-base md:text-lg font-black text-[#111827]">Rekap Barang / Jasa</h2>
        <p className="text-xs text-[#6B7280]">{BULAN[Number(ym.slice(5, 7)) - 1]} {ym.slice(0, 4)} · terpisah dari tagihan internet bulanan. Invoice VOID dan pembayaran yang di-void tidak dihitung.</p>
      </div>
      {err && <div className="text-xs text-[#B91C1C]" data-testid="nb-rekap-error">{err}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card label="Tagihan Barang/Jasa Terbit" value={formatRupiah(cur.terbit_total || 0)} hint={`${cur.terbit_count || 0} invoice · berdasarkan tanggal invoice`}
              Icon={ReceiptText} cls="bg-[#EFF6FF] border-[#BFDBFE] text-[#1E3A8A]" testid="nb-rekap-terbit" />
        <Card label="Uang Diterima Barang/Jasa" value={formatRupiah(cur.diterima_total || 0)} hint={`${cur.diterima_count || 0} pembayaran · berdasarkan tanggal bayar`}
              Icon={Wallet} cls="bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]" testid="nb-rekap-diterima" />
      </div>
      <div className="h-[220px]" data-testid="nb-rekap-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} width={48} />
            <Tooltip formatter={(v, n) => [formatRupiah(v), n]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="terbit_total" name="Tagihan terbit" fill="#3B82F6" radius={[3, 3, 0, 0]} />
            <Bar dataKey="diterima_total" name="Uang diterima" fill="#10B981" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};
